'use client';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vendorApi } from '../../services/vendorApi';
import { categoryApi } from '../../services/categoryApi';
import { VendorFormData, BusinessType, VendorStatus, ApprovalStatus } from '../../types/vendor';
import { Building2, User, MapPin, FileText, CreditCard, Settings, Users, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { courierApi } from '../../services/courierApi';

interface Category {
  id: string;
  name: string;
  parentId?: string;
}

export default function VendorFormPage() {
  const params = useParams();
  const navigate = useNavigate();
  const vendorId = params.id as string | undefined;
  const isEditMode = !!vendorId;

  const [formData, setFormData] = useState<VendorFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    fullAddress: '',
    province: '',
    district: '',
    neighborhood: '',
    postalCode: '',
    businessType: 'individual',
    companyName: '',
    taxOffice: '',
    taxNumber: '',
    bankName: '',
    iban: '',
    accountHolderName: '',
    logoUrl: '',
    description: '',
    website: '',
    commissionRate: 15,
    courierDeliveryFee: 0,
    status: 'pending',
    approvalStatus: 'pending',
    assignedCategories: [],
    agreementType: 'revenue_commission',
    platformShareRate: 15,
    paymentCollectionMethod: 'vendor_pos',
    settlementPeriod: 'monthly',
    isPartner: false,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [couriers, setCouriers] = useState<any[]>([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [assignedCouriers, setAssignedCouriers] = useState<any[]>([]);
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [selectedCourierIds, setSelectedCourierIds] = useState<string[]>([]);

  // Kategorileri yükle
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoryApi.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Edit mode: vendor'ı yükle
  useEffect(() => {
    if (isEditMode && vendorId) {
      const fetchVendor = async () => {
        try {
          setLoading(true);
          const vendor = await vendorApi.getVendor(vendorId);
          setFormData({
            firstName: vendor.firstName,
            lastName: vendor.lastName,
            email: vendor.email,
            password: '',
            phone: vendor.phone,
            fullAddress: vendor.address.fullAddress,
            province: vendor.address.province,
            district: vendor.address.district,
            neighborhood: vendor.address.neighborhood,
            postalCode: vendor.address.postalCode,
            businessType: vendor.taxInfo.businessType,
            companyName: vendor.taxInfo.companyName,
            taxOffice: vendor.taxInfo.taxOffice,
            taxNumber: vendor.taxInfo.taxNumber,
            bankName: vendor.bankInfo.bankName,
            iban: vendor.bankInfo.iban,
            accountHolderName: vendor.bankInfo.accountHolderName,
            logoUrl: vendor.logoUrl,
            description: vendor.description,
            website: vendor.website,
            commissionRate: vendor.commissionRate,
            courierDeliveryFee: vendor.courierDeliveryFee || 0,
            status: vendor.status,
            approvalStatus: vendor.approvalStatus,
            assignedCategories: vendor.assignedCategories,
            agreementType: vendor.agreementType || 'revenue_commission',
            platformShareRate: vendor.platformShareRate ?? vendor.commissionRate ?? 15,
            paymentCollectionMethod: vendor.paymentCollectionMethod || 'vendor_pos',
            settlementPeriod: vendor.settlementPeriod || 'monthly',
            isPartner: vendor.isPartner || false,
          });
        } catch (error) {
          toast.error('Vendor yüklenemedi');
          navigate('/admin/vendors');
        } finally {
          setLoading(false);
        }
      };
      fetchVendor();
    }
  }, [isEditMode, vendorId, navigate]);

  // Kuryeları yükle (courier tab'ı açılınca)
  const loadCouriers = async () => {
    if (!vendorId) return;
    
    setLoadingCouriers(true);
    try {
      // Tüm kuryeları getir
      const couriersResponse = await courierApi.getAllCouriers({
        status: 'active',
        limit: 1000,
        offset: 0,
      });
      setCouriers(couriersResponse.couriers || []);

      // Vendor'a atanmış kuryeları getir
      const vendorCouriersResponse = await courierApi.getVendorCouriers(vendorId);
      const assignedCouriersList = vendorCouriersResponse.couriers || [];
      setAssignedCouriers(assignedCouriersList);
    } catch (error: any) {
      console.error('Kuryeler yüklenemedi:', error);
      toast.error('Kuryeler yüklenemedi');
    } finally {
      setLoadingCouriers(false);
    }
  };

  const handleCourierToggle = (courierId: string) => {
    setSelectedCourierIds((prev) => {
      if (prev.includes(courierId)) {
        return prev.filter((id) => id !== courierId);
      } else {
        return [...prev, courierId];
      }
    });
  };

  const handleAddCouriers = async () => {
    if (!vendorId || selectedCourierIds.length === 0) return;
    
    try {
      setSubmitting(true);
      // Atanmış kuryelerle yeni seçilen kuryeleri birleştir
      const allCourierIds = [
        ...assignedCouriers.map(c => c.id),
        ...selectedCourierIds
      ];
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/vendors/${vendorId}/assign-couriers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
          },
          body: JSON.stringify({ courierIds: allCourierIds })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kuryeler atanırken hata oluştu');
      }

      toast.success('Kuryeler başarıyla eklendi');
      setShowCourierModal(false);
      setSelectedCourierIds([]);
      // Atanmış kuryeları yenile
      await loadCouriers();
    } catch (error: any) {
      console.error('Kurye ekleme hatası:', error);
      toast.error(error.message || 'Kuryeler eklenirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveCourier = async (courierId: string) => {
    if (!vendorId) return;
    
    try {
      setSubmitting(true);
      const allCourierIds = assignedCouriers
        .map(c => c.id)
        .filter(id => id !== courierId);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/vendors/${vendorId}/assign-couriers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
          },
          body: JSON.stringify({ courierIds: allCourierIds })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kurye kaldırılırken hata oluştu');
      }

      toast.success('Kurye başarıyla kaldırıldı');
      // Atanmış kuryeları yenile
      await loadCouriers();
    } catch (error: any) {
      console.error('Kurye kaldırma hatası:', error);
      toast.error(error.message || 'Kurye kaldırılırken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'Ad zorunludur';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Soyad zorunludur';
    if (!formData.email?.trim()) newErrors.email = 'Email zorunludur';
    if (!isEditMode && !formData.password?.trim()) newErrors.password = 'Şifre zorunludur';
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }
    if (!formData.phone?.trim()) newErrors.phone = 'Telefon zorunludur';
    if (!formData.fullAddress?.trim()) newErrors.fullAddress = 'Adres zorunludur';
    if (!formData.province?.trim()) newErrors.province = 'İl zorunludur';
    if (!formData.district?.trim()) newErrors.district = 'İlçe zorunludur';
    if (!formData.neighborhood?.trim()) newErrors.neighborhood = 'Semt zorunludur';
    if (!formData.taxOffice?.trim()) newErrors.taxOffice = 'Vergi dairesi zorunludur';
    if (!formData.taxNumber?.trim()) newErrors.taxNumber = 'Vergi numarası zorunludur';
    if (!formData.bankName?.trim()) newErrors.bankName = 'Banka adı zorunludur';
    if (!formData.iban?.trim()) newErrors.iban = 'IBAN zorunludur';
    if (!formData.accountHolderName?.trim()) newErrors.accountHolderName = 'Hesap sahibi adı zorunludur';

    if (formData.businessType === 'company' && !formData.companyName?.trim()) {
      newErrors.companyName = 'Şirket adı zorunludur';
    }

    setFieldErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      if (isEditMode && vendorId) {
        const updatePayload = formData.password.trim()
          ? formData
          : (() => {
              const { password, ...rest } = formData;
              return rest;
            })();
        await vendorApi.updateVendor(vendorId, updatePayload);
        toast.success('Vendor güncellendi');
      } else {
        await vendorApi.createVendor(formData);
        toast.success('Vendor oluşturuldu');
      }
      navigate('/admin/vendors');
    } catch (error: any) {
      toast.error(error.message || (isEditMode ? 'Vendor güncellenemedi' : 'Vendor oluşturulamadı'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setFormData({
      ...formData,
      assignedCategories: formData.assignedCategories.includes(categoryId)
        ? formData.assignedCategories.filter(id => id !== categoryId)
        : [...formData.assignedCategories, categoryId],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Vendor Düzenle' : 'Yeni Vendor Ekle'}
          </h1>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/vendors')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
            >
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'basic', label: 'Temel Bilgiler', icon: User },
              { id: 'address', label: 'Adres Bilgileri', icon: MapPin },
              { id: 'tax', label: 'Vergi & Şirket', icon: FileText },
              { id: 'bank', label: 'Banka Bilgileri', icon: CreditCard },
              { id: 'settings', label: 'Platform Ayarları', icon: Settings },
              ...(isEditMode ? [{ id: 'couriers', label: 'Kuryeler', icon: Users }] : []),
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'couriers') {
                    loadCouriers();
                  }
                }}
                className={`px-4 py-3 border-b-2 font-medium whitespace-nowrap transition flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* TEMEL BİLGİLER */}
          {activeTab === 'basic' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Ad *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    placeholder="Ad"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.firstName
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Soyad *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    placeholder="Soyad"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.lastName
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@example.com"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.email
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                    disabled={isEditMode} // Edit mode'da email değiştirilemez
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {isEditMode ? 'Yeni Şifre (Opsiyonel)' : 'Şifre *'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder={isEditMode ? 'Değiştirmek istemiyorsanız boş bırakın' : 'En az 6 karakter'}
                    minLength={6}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.password
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required={!isEditMode}
                  />
                  {fieldErrors.password && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.password}</p>
                  )}
                </div>

                {!isEditMode && null}

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Telefon *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+90 532 123 4567"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.phone
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Logo URL (Opsiyonel)</label>
                <input
                  type="url"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({...formData, logoUrl: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Website (Opsiyonel)</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://www.example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Açıklama (Opsiyonel)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Vendor hakkında kısa açıklama"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* ADRES BİLGİLERİ */}
          {activeTab === 'address' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Tam Adres *</label>
                <textarea
                  value={formData.fullAddress}
                  onChange={(e) => setFormData({...formData, fullAddress: e.target.value})}
                  placeholder="Mahalle, sokak, bina no, daire no..."
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    fieldErrors.fullAddress
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  rows={3}
                  required
                />
                {fieldErrors.fullAddress && (
                  <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.fullAddress}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">İl *</label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                    placeholder="İstanbul"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.province
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.province && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.province}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">İlçe *</label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData({...formData, district: e.target.value})}
                    placeholder="Kadıköy"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.district
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.district && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.district}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Semt *</label>
                  <input
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData({...formData, neighborhood: e.target.value})}
                    placeholder="Acıbadem"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.neighborhood
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.neighborhood && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.neighborhood}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Posta Kodu (Opsiyonel)</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                  placeholder="34000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* VERGİ & ŞİRKET BİLGİLERİ */}
          {activeTab === 'tax' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">İşletme Tipi *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="individual"
                      checked={formData.businessType === 'individual'}
                      onChange={(e) => setFormData({...formData, businessType: e.target.value as BusinessType})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">Şahıs</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="company"
                      checked={formData.businessType === 'company'}
                      onChange={(e) => setFormData({...formData, businessType: e.target.value as BusinessType})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">Şirket</span>
                  </label>
                </div>
              </div>

              {formData.businessType === 'company' && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Şirket Adı *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    placeholder="ABC Gıda A.Ş."
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.companyName
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.companyName && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.companyName}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Vergi Dairesi *</label>
                  <input
                    type="text"
                    value={formData.taxOffice}
                    onChange={(e) => setFormData({...formData, taxOffice: e.target.value})}
                    placeholder="Kadıköy Vergi Dairesi"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.taxOffice
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.taxOffice && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.taxOffice}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Vergi Numarası *</label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={(e) => setFormData({...formData, taxNumber: e.target.value})}
                    placeholder="1234567890"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.taxNumber
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.taxNumber && (
                    <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.taxNumber}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* BANKA BİLGİLERİ */}
          {activeTab === 'bank' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Banka Adı *</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                  placeholder="Ziraat Bankası"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    fieldErrors.bankName
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  required
                />
                {fieldErrors.bankName && (
                  <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.bankName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">IBAN *</label>
                <input
                  type="text"
                  value={formData.iban}
                  onChange={(e) => setFormData({...formData, iban: e.target.value})}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    fieldErrors.iban
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  required
                />
                {fieldErrors.iban && (
                  <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.iban}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Hesap Sahibi Adı *</label>
                <input
                  type="text"
                  value={formData.accountHolderName}
                  onChange={(e) => setFormData({...formData, accountHolderName: e.target.value})}
                  placeholder="Ad Soyad veya Şirket Adı"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    fieldErrors.accountHolderName
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  required
                />
                {fieldErrors.accountHolderName && (
                  <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.accountHolderName}</p>
                )}
              </div>
            </div>
          )}

          {/* PLATFORM AYARLARI */}
          {activeTab === 'settings' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              {/* Anlaşma Tipi */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Anlaşma Tipi</label>
                <select
                  value={formData.agreementType}
                  onChange={(e) => setFormData({...formData, agreementType: e.target.value as any})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="revenue_commission">Ciro Komisyonu (Brüt ciro üzerinden %)</option>
                  <option value="profit_share">Kâr Ortaklığı (Net kâr üzerinden %)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.agreementType === 'profit_share'
                    ? 'Masraflar (kurye, malzeme, POS komisyonu, platform giderleri) düşüldükten sonra net kâr üzerinden pay alınır'
                    : 'Brüt ciro üzerinden sabit komisyon oranı alınır'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Platform Pay Oranı (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.platformShareRate}
                    onChange={(e) => setFormData({...formData, platformShareRate: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.agreementType === 'profit_share'
                      ? `Net kârın %${formData.platformShareRate}'i platform'a aittir`
                      : `Cironun %${formData.platformShareRate}'i platform komisyonudur`}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Komisyon Oranı (%) <span className="text-gray-400 font-normal">(eski)</span></label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.commissionRate}
                    onChange={(e) => setFormData({...formData, commissionRate: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Geriye uyumluluk için. Yeni sistemde "Platform Pay Oranı" kullanılır.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Ödeme Toplama</label>
                  <select
                    value={formData.paymentCollectionMethod}
                    onChange={(e) => setFormData({...formData, paymentCollectionMethod: e.target.value as any})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="vendor_pos">Vendor POS (Vendor'un sanal POS'u)</option>
                    <option value="platform_pos">Platform POS (Platform'un sanal POS'u)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Mutabakat Periyodu</label>
                  <select
                    value={formData.settlementPeriod}
                    onChange={(e) => setFormData({...formData, settlementPeriod: e.target.value as any})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Aylık</option>
                    <option value="weekly">Haftalık</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPartner}
                    onChange={(e) => setFormData({...formData, isPartner: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-semibold text-gray-700">Ortak Vendor</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Bu vendor ile özel ortaklık ilişkisi var (kâr paylaşımı, POS kullanımı vb.)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Durum</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as VendorStatus})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Beklemede</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">İnaktif</option>
                    <option value="suspended">Askıya Alındı</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Onay Durumu</label>
                  <select
                    value={formData.approvalStatus}
                    onChange={(e) => setFormData({...formData, approvalStatus: e.target.value as ApprovalStatus})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Beklemede</option>
                    <option value="approved">Onaylandı</option>
                    <option value="rejected">Reddedildi</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Atanan Kategoriler</label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                  {categories.length === 0 ? (
                    <p className="text-sm text-gray-500">Kategori bulunamadı</p>
                  ) : (
                    categories.map(cat => (
                      <label key={cat.id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.assignedCategories.includes(cat.id)}
                          onChange={() => handleCategoryToggle(cat.id)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm text-gray-700">{cat.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* KURYELER */}
          {activeTab === 'couriers' && (
            <div className="bg-white rounded-lg p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  Atanmış Kuryeler ({assignedCouriers.length})
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowCourierModal(true);
                    setSelectedCourierIds([]);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + Kurye Ekle
                </button>
              </div>

              {loadingCouriers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : assignedCouriers.length === 0 ? (
                <p className="text-center text-gray-600 py-8">Henüz kurye atanmamış</p>
              ) : (
                <div className="space-y-2">
                  {assignedCouriers.map((courier) => (
                    <div
                      key={courier.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {courier.firstName} {courier.lastName}
                        </p>
                        <p className="text-xs text-gray-600">
                          {courier.phone}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCourier(courier.id)}
                        disabled={submitting}
                        className="px-3 py-1 text-red-600 hover:bg-red-100 rounded transition disabled:opacity-50"
                      >
                        Kaldır
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Modal */}
              {showCourierModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 flex flex-col">
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-xl font-bold text-gray-800">Kurye Seç</h2>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-2">
                      {couriers.length === 0 ? (
                        <p className="text-center text-gray-600 py-8">Kurye bulunamadı</p>
                      ) : (
                        couriers
                          .filter(c => !assignedCouriers.some(ac => ac.id === c.id))
                          .map((courier) => (
                            <div
                              key={courier.id}
                              onClick={() => handleCourierToggle(courier.id)}
                              className={`p-3 border rounded-lg cursor-pointer transition ${
                                selectedCourierIds.includes(courier.id)
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-300 hover:border-green-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    selectedCourierIds.includes(courier.id)
                                      ? 'border-green-500 bg-green-500'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {selectedCourierIds.includes(courier.id) && (
                                    <Check size={16} className="text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-800">
                                    {courier.firstName} {courier.lastName}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {courier.phone}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                      )}
                    </div>

                    <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowCourierModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        İptal
                      </button>
                      <button
                        type="button"
                        onClick={handleAddCouriers}
                        disabled={submitting || selectedCourierIds.length === 0}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition"
                      >
                        {submitting ? 'Ekleniyor...' : `Ekle (${selectedCourierIds.length})`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/vendors')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
            >
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
